// Immersive product template with vertical card deck

class ProductImmersive extends HTMLElement {
  constructor() {
    super();

    this.productData = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.variants = this.productData.variants;
    this.currentVariantIdx = 0;

    // Map variant ID to gallery media ID
    this.variantMediaMap = {};
    const mediaMapEl = this.querySelector('[data-variant-media-map]');
    if (mediaMapEl) {
      try {
        this.variantMediaMap = JSON.parse(mediaMapEl.textContent) || {};
      } catch (e) {
        this.variantMediaMap = {};
      }
    }

    // Initial variant from URL or default
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      const idx = this.variants.findIndex(v => v.id.toString() === urlVariantId);
      if (idx > -1) this.currentVariantIdx = idx;
    }

    this.allGallerySlides = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-item]'));
    this.allBlockSlides = Array.prototype.slice.call(this.querySelectorAll('.sip__slot--collection-grid, .sip__slot--footer-native'));
    this.hasAltFilter = this.allGallerySlides.some(s => s.hasAttribute('data-variant-ids') || (s.getAttribute('data-alt') || '').indexOf('#') !== -1);

    this.cartBtn = this.querySelector('[data-cart-btn], [data-drawer-toggle="drawer-cart"]');
    this.cartPill = this.querySelector('[data-cart-pill]');
    this.cartPillThumb = this.querySelector('[data-cart-pill-thumb]');
    this.cartBadge = this.querySelector('[data-cart-badge]');
    this.backBtn = this.querySelector('[data-back-button]');

    this.atcBarBtn = this.querySelector('.pi__atc-bar-btn');
    this.atcBarText = this.querySelector('[data-atc-bar-text]');
    this.atcDefaultHtml = (this.atcBarText && this.atcBarText.innerHTML) || 'Add to Cart';
    this.atcDefaultText = (this.atcBarText && this.atcBarText.textContent.trim()) || 'Add to Cart';

    this.bottomUI = this.querySelector('.pi__bottom');
    this.productTitleEl = this.querySelector('[data-product-title]');
    this.priceEls = this.querySelectorAll('[data-product-price]');
    this.comparePriceEls = this.querySelectorAll('[data-compare-price]');
    this.discountBadgeEls = this.querySelectorAll('[data-discount-badge]');
    this.galleryContainer = this.querySelector('[data-gallery]');

    this.dropletNav = this.querySelector('[data-droplet-nav]');
    this.dropletDots = this.querySelectorAll('[data-droplet-dot]');

    this.deck = null;

    this.barSwatchContainer = this.querySelector('[data-bar-swatches]');
    this.barSwatchBtns = this.querySelectorAll('[data-bar-swatch]');
    this.barPrev = this.querySelector('[data-bar-prev]');
    this.barNext = this.querySelector('[data-bar-next]');

    this.backdrop = this.querySelector('[data-backdrop]');
    this.cartSheet = this.querySelector('[data-cart-sheet]');
    this.detailsSheet = this.querySelector('[data-details-sheet]');

    this.sheetSwatchBtns = this.querySelectorAll('[data-sheet-swatch]');
    this.sheetThumb = this.querySelector('[data-sheet-thumb]');
    this.sheetColorName = this.querySelector('[data-sheet-color-name]');
    this.sheetPriceEl = this.querySelector('[data-sheet-price]');
    this.qtyValEl = this.querySelector('[data-qty-val]');
    this.qtyMinus = this.querySelector('[data-qty-minus]');
    this.qtyPlus = this.querySelector('[data-qty-plus]');
    this.totalValEl = this.querySelector('[data-total-val]');
    this.confirmBtn = this.querySelector('[data-confirm-btn]');
    this.confirmBtnText = this.querySelector('[data-confirm-text]');
    this.hiddenIdInput = this.querySelector('input[name="id"]');
    this.hiddenQtyInput = this.querySelector('input[name="quantity"]');
    this.form = this.querySelector('form[data-product-form]');

    this.accordions = this.querySelectorAll('[data-accordion]');

    this.quantity = 1;
    this.cartPillTimeout = null;

    this._initDeck();
    this._bind();
    this._syncUI();
    this._updateCartBadge();
  }

  _initDeck() {
    const deckEl = this.querySelector('[data-deck]');
    if (!deckEl) return;

    const start = () => {
      if (typeof window.CardDeck === 'undefined') {
        requestAnimationFrame(start);
        return;
      }

      // Hide buy bar before non-product slide arrives
      deckEl.addEventListener('deck:change', (e) => {
        this._checkBottomVisibility();
        this._updateDropletNav(e.detail && e.detail.index);
      });
      deckEl.addEventListener('deck:dragstart', (e) => {
        const tidx = e.detail && e.detail.targetIndex;
        this._checkBottomVisibility(tidx);
        if (typeof tidx === 'number') this._updateDropletNav(tidx);
      });
      deckEl.addEventListener('deck:dragend', (e) => {
        // Restore bar if drag canceled
        if (e.detail && !e.detail.committed) {
          this._checkBottomVisibility();
          this._updateDropletNav();
        }
      });

      this._filterGalleryForVariant(this.variants[this.currentVariantIdx], { animate: false });

      this._onDeckResize = () => this._markScrollableSlides();
      window.addEventListener('resize', this._onDeckResize, { passive: true });
    };

    start();
  }

  // Sequentially prefetch upcoming slide images during idle frames
  _initBackgroundPreloader() {
    const warmNextImages = () => {
      if (!this.deck || this.deck.destroyed) return;
      const lazyImgs = Array.prototype.slice.call(this.querySelectorAll('.pi-deck img[loading="lazy"]'));
      if (!lazyImgs.length) return;

      let idx = 0;
      const loadNext = () => {
        if (idx >= lazyImgs.length) return;
        const img = lazyImgs[idx++];
        if (img.dataset.preloaded) {
          loadNext();
          return;
        }
        img.dataset.preloaded = 'true';
        const dummy = new Image();
        dummy.decoding = 'async';
        if (img.srcset) dummy.srcset = img.srcset;
        if (img.sizes) dummy.sizes = img.sizes;
        dummy.src = img.src || img.currentSrc;
        dummy.onload = dummy.onerror = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadNext, { timeout: 800 });
          } else {
            setTimeout(loadNext, 120);
          }
        };
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadNext, { timeout: 1200 });
      } else {
        setTimeout(loadNext, 300);
      }
    };

    const firstImg = this.querySelector('.pi-deck img[loading="eager"]') || this.querySelector('.pi-deck img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', () => setTimeout(warmNextImages, 800), { once: true });
    } else {
      setTimeout(warmNextImages, 1200);
    }
  }

  _markScrollableSlides() {
    if (!this.deck) return;
    this.deck.slides.forEach((slide) => {
      if (!slide.hasAttribute('data-deck-scrollable')) return;
      const overflows = slide.scrollHeight > slide.clientHeight + 4;
      slide.classList.toggle('deck__slide--scrollable', overflows);
    });
  }

  _bind() {
    // --- Back Button Navigation ---
    if (this.backBtn) {
      this.backBtn.addEventListener('click', (e) => {
        if (document.referrer && document.referrer.indexOf(window.location.host) !== -1) {
          e.preventDefault();
          window.history.back();
        }
      });
    }

    // Bar swatch selection
    this.barSwatchBtns.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        this._selectVariant(idx);
      });
    });

    // Bar chevrons
    if (this.barPrev) {
      this.barPrev.addEventListener('click', () => {
        const nextIdx = this.currentVariantIdx > 0 ? this.currentVariantIdx - 1 : this.variants.length - 1;
        this._selectVariant(nextIdx);
      });
    }
    if (this.barNext) {
      this.barNext.addEventListener('click', () => {
        const nextIdx = this.currentVariantIdx < this.variants.length - 1 ? this.currentVariantIdx + 1 : 0;
        this._selectVariant(nextIdx);
      });
    }

    // Sheet swatch selection
    this.sheetSwatchBtns.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        this._selectVariant(idx);
      });
    });

    // Open sheets
    this.querySelector('[data-open-cart-sheet]')?.addEventListener('click', () => {
      this.addedSuccess = false;
      this._syncUI();
      this._openSheet(this.cartSheet);
    });
    this.querySelector('[data-open-details-sheet]')?.addEventListener('click', () => {
      this._openSheet(this.detailsSheet);
    });

    // ATC button pointer tracking
    if (this.atcBarBtn) {
      this._onAtcPointerMove = (e) => {
        const rect = this.atcBarBtn.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        this.atcBarBtn.style.setProperty('--pointer-x', `${x}px`);
        this.atcBarBtn.style.setProperty('--pointer-y', `${y}px`);
      };
      this._onAtcPointerLeave = () => {
        this.atcBarBtn.style.setProperty('--pointer-transition', 'left 0.4s ease, top 0.4s ease');
        this.atcBarBtn.style.removeProperty('--pointer-x');
        this.atcBarBtn.style.removeProperty('--pointer-y');
        setTimeout(() => {
          if (this.atcBarBtn) this.atcBarBtn.style.removeProperty('--pointer-transition');
        }, 400);
      };
      this._onAtcPointerEnter = () => {
        this.atcBarBtn.style.removeProperty('--pointer-transition');
      };

      this.atcBarBtn.addEventListener('pointermove', this._onAtcPointerMove, { passive: true });
      this.atcBarBtn.addEventListener('pointerleave', this._onAtcPointerLeave);
      this.atcBarBtn.addEventListener('pointerenter', this._onAtcPointerEnter);
    }

    // Close sheets
    this.backdrop?.addEventListener('click', () => this._closeAllSheets());
    this.querySelectorAll('[data-close-sheet]').forEach(btn => {
      btn.addEventListener('click', () => this._closeAllSheets());
    });
    this._onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this._closeAllSheets();
      }
    };
    document.addEventListener('keydown', this._onKeyDown);

    // Quantity controls
    this.qtyMinus?.addEventListener('click', () => {
      if (this.quantity > 1) {
        this.quantity--;
        this._updateQtyUI();
      }
    });
    this.qtyPlus?.addEventListener('click', () => {
      this.quantity++;
      this._updateQtyUI();
    });

    // Confirm add to cart
    this.confirmBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._addToCart();
    });

    // Accordions
    this.accordions.forEach(acc => {
      const trigger = acc.querySelector('[data-accordion-trigger]');
      trigger?.addEventListener('click', () => {
        const isOpen = acc.classList.contains('is-open');
        acc.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', !isOpen);
        const plusIcon = trigger.querySelector('[data-icon-plus]');
        const minusIcon = trigger.querySelector('[data-icon-minus]');
        if (plusIcon && minusIcon) {
          plusIcon.style.display = isOpen ? '' : 'none';
          minusIcon.style.display = isOpen ? 'none' : '';
        }
      });
    });

    // Cart button and pill click
    this.cartBtn?.addEventListener('click', (e) => this._openCart(e));
    this.cartPill?.addEventListener('click', (e) => this._openCart(e));

    // Cart badge updates
    const handleCartChange = (e) => {
      if (e && e.detail && e.detail.cart && typeof e.detail.cart.item_count !== 'undefined') {
        this._updateCartBadgeCount(e.detail.cart.item_count);
      } else {
        this._updateCartBadge();
      }
    };
    document.addEventListener('theme:cart:change', handleCartChange);
    document.addEventListener('theme:cart:reload', () => this._updateCartBadge());
    document.addEventListener('theme:cart:popdown', () => this._updateCartBadge());
    document.addEventListener('cart:updated', () => this._updateCartBadge());

    // Footer accordions
    this._initFooterAccordions();
  }

  _initFooterAccordions() {
    this._onFooterAccordionClick = (e) => {
      if (window.innerWidth >= 768) return;
      const trigger = e.target.closest('[data-sip-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const col = trigger.closest('.sip__footer-col');
      if (!col) return;
      const body = col.querySelector('[data-sip-accordion-body]');
      if (!body) return;

      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isExpanded);
      trigger.classList.toggle('is-open', !isExpanded);
      body.classList.toggle('is-open', !isExpanded);

      // Recalculate slide scrollability on accordion toggle
      if (this.deck) {
        this.deck.update();
        setTimeout(() => this._markScrollableSlides(), 380);
      }
    };
    this.addEventListener('click', this._onFooterAccordionClick);
  }

  // Buy bar visibility over product image cards
  _checkBottomVisibility(forIndex) {
    if (!this.deck || !this.bottomUI) return;

    const idx = typeof forIndex === 'number' ? forIndex : this.deck.index;
    const slide = this.deck.slides[idx] || null;
    if (!slide) return;

    const kind = slide.getAttribute('data-slide-kind') || '';
    const isNonProduct = kind === 'collection-grid' || kind === 'footer' ||
      slide.classList.contains('sip__slot--collection-grid') ||
      slide.classList.contains('sip__slot--footer-native');
    const isLastSlide = idx >= this.deck.slides.length - 1;

    this.bottomUI.classList.toggle('is-hidden-last-slide', isLastSlide || isNonProduct);
  }

  _selectVariant(idx) {
    if (idx === this.currentVariantIdx) return;
    this.currentVariantIdx = idx;
    const variant = this.variants[idx];
    const btn = this.barSwatchBtns[idx];
    if (btn) btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    this._syncUI();
    this._filterGalleryForVariant(variant, { animate: true });
  }

  // UI sync
  _syncUI() {
    const variant = this.variants[this.currentVariantIdx];
    if (!variant) return;

    if (this.hiddenIdInput) this.hiddenIdInput.value = variant.id;

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (this.productTitleEl && this.productData && this.productData.title) {
      this.productTitleEl.textContent = this.productData.title;
    }

    const formatted = this._formatMoney(variant.price);
    this.priceEls.forEach(el => el.textContent = formatted);

    const hasDiscount = variant.compare_at_price > variant.price;
    const compareFormatted = hasDiscount ? this._formatMoney(variant.compare_at_price) : '';
    this.comparePriceEls.forEach(el => {
      el.textContent = compareFormatted;
      el.classList.toggle('is-hidden', !hasDiscount);
    });

    this.discountBadgeEls.forEach(badge => {
      badge.classList.toggle('is-hidden', !hasDiscount);
      if (hasDiscount) {
        const type = badge.getAttribute('data-badge-type') || 'percentage';
        const savingsCents = variant.compare_at_price - variant.price;
        if (type === 'percentage') {
          const pct = Math.round((savingsCents / variant.compare_at_price) * 100);
          badge.textContent = `Save ${pct}%`;
        } else {
          badge.textContent = `Save ${this._formatMoney(savingsCents)}`;
        }
      }
    });

    // Update bar swatches
    this.barSwatchBtns.forEach((btn, i) => {
      const rect = btn.querySelector('[data-swatch-rect]');
      const label = btn.querySelector('[data-swatch-label]');
      if (i === this.currentVariantIdx) {
        rect?.classList.replace('pi__swatch-rect--inactive', 'pi__swatch-rect--active');
        label?.classList.replace('pi__swatch-label--inactive', 'pi__swatch-label--active');
      } else {
        rect?.classList.replace('pi__swatch-rect--active', 'pi__swatch-rect--inactive');
        label?.classList.replace('pi__swatch-label--active', 'pi__swatch-label--inactive');
      }
    });

    // Update sheet swatches
    this.sheetSwatchBtns.forEach((btn, i) => {
      if (i === this.currentVariantIdx) {
        btn.classList.replace('pi__sheet-swatch--inactive', 'pi__sheet-swatch--active');
      } else {
        btn.classList.replace('pi__sheet-swatch--active', 'pi__sheet-swatch--inactive');
      }
    });

    // Update sheet summary
    if (this.sheetColorName) this.sheetColorName.textContent = variant.title;
    if (this.sheetPriceEl) this.sheetPriceEl.textContent = formatted;
    // Image URL fallback
    if (this.sheetThumb) {
      const featured = variant.featured_image;
      const preview = variant.featured_media && variant.featured_media.preview_image;
      const mediaSrc = (featured && featured.src) || (preview && preview.src) || '';
      if (mediaSrc) this.sheetThumb.src = mediaSrc;
    }

    this._updateGallery(variant);
    this._updateQtyUI();
    this._checkBottomVisibility();

    // Update bottom bar button
    if (this.atcBarBtn) {
      if (!variant.available) {
        this.atcBarBtn.disabled = true;
        this.atcBarBtn.classList.add('is-sold-out');
        if (this.atcBarText) {
          this.atcBarText.textContent = 'Sold Out';
        } else {
          this.atcBarBtn.textContent = 'Sold Out';
        }
      } else {
        this.atcBarBtn.disabled = false;
        this.atcBarBtn.classList.remove('is-sold-out');
        if (this.atcBarText) {
          this.atcBarText.textContent = this.atcDefaultText || 'Add to Cart';
        } else {
          this.atcBarBtn.textContent = this.atcDefaultText || 'Add to Cart';
        }
      }
    }

    // Update confirm button
    if (this.confirmBtn) {
      if (!variant.available) {
        this.confirmBtn.disabled = true;
        this.confirmBtn.classList.remove('pi__confirm-btn--default');
        this.confirmBtn.classList.add('pi__confirm-btn--disabled');
        if (this.confirmBtnText) {
          this.confirmBtnText.textContent = 'Sold Out';
        } else {
          this.confirmBtn.innerHTML = '<span data-confirm-text>Sold Out</span>';
          this.confirmBtnText = this.confirmBtn.querySelector('[data-confirm-text]');
        }
      } else {
        this.confirmBtn.disabled = false;
        this.confirmBtn.classList.remove('pi__confirm-btn--disabled');
        this.confirmBtn.classList.add('pi__confirm-btn--default');
        const total = this._formatMoney(variant.price * this.quantity);
        if (this.confirmBtnText) {
          this.confirmBtnText.textContent = `Confirm Add to Bag \u2022 ${total}`;
        } else {
          this.confirmBtn.innerHTML = `<span data-confirm-text>Confirm Add to Bag \u2022 ${total}</span>`;
          this.confirmBtnText = this.confirmBtn.querySelector('[data-confirm-text]');
        }
      }
    }

    // Dispatch Pipeline variant change event
    this.dispatchEvent(new CustomEvent('theme:variant:change', {
      detail: { variant: variant },
      bubbles: true
    }));
  }

  _updateQtyUI() {
    if (this.qtyValEl) this.qtyValEl.textContent = this.quantity;
    if (this.hiddenQtyInput) this.hiddenQtyInput.value = this.quantity;

    const variant = this.variants[this.currentVariantIdx];
    if (variant) {
      const total = this._formatMoney(variant.price * this.quantity);
      if (this.totalValEl) this.totalValEl.textContent = total;
      if (this.confirmBtnText && !this.addedSuccess) {
        if (variant.available) {
          this.confirmBtnText.textContent = `Confirm Add to Bag \u2022 ${total}`;
        } else {
          this.confirmBtnText.textContent = 'Sold Out';
        }
      }
    }
  }

  // Resolve gallery media ID for variant
  _mediaIdForVariant(variant) {
    if (!variant) return '';
    const mapped = this.variantMediaMap[variant.id];
    if (mapped) return String(mapped);
    if (variant.featured_media && variant.featured_media.id) {
      return String(variant.featured_media.id);
    }
    return '';
  }

  // Filter gallery slides by variant ID
  _filterGalleryForVariant(variant, opts = {}) {
    if (!this.allGallerySlides || !this.allGallerySlides.length) return;
    const deckEl = this.querySelector('[data-deck]');
    if (!deckEl) return;

    const variantIdStr = String(variant.id);
    const targetMediaId = this._mediaIdForVariant(variant);
    let matchedCount = 0;

    this.allGallerySlides.forEach((slide) => {
      const isShared = slide.hasAttribute('data-shared');
      const varIdsAttr = slide.getAttribute('data-variant-ids') || '';
      const varIds = varIdsAttr ? varIdsAttr.split(',') : [];
      const mediaId = slide.getAttribute('data-media-id') || '';

      const matches = isShared ||
        varIds.includes(variantIdStr) ||
        (targetMediaId && mediaId === targetMediaId) ||
        (variant.featured_media && mediaId === String(variant.featured_media.id)) ||
        (!slide.hasAttribute('data-variant-ids') && !slide.hasAttribute('data-shared'));

      if (matches) {
        slide.style.display = '';
        slide.setAttribute('data-deck-slide', '');
        matchedCount++;
      } else {
        slide.style.display = 'none';
        slide.removeAttribute('data-deck-slide');
      }
    });

    // Fallback to all slides if none matched
    if (matchedCount === 0) {
      this.allGallerySlides.forEach((slide) => {
        slide.style.display = '';
        slide.setAttribute('data-deck-slide', '');
      });
      matchedCount = this.allGallerySlides.length;
    }

    // Re-index active slides in DOM order
    const activeSlides = Array.prototype.slice.call(deckEl.querySelectorAll('[data-deck-slide]'));
    activeSlides.forEach((slide, idx) => {
      slide.style.zIndex = String(idx + 1);
      slide.style.setProperty('--slot-index', String(idx + 1));
    });

    const activeGalleryCount = this.allGallerySlides.filter(s => s.style.display !== 'none').length;
    this._rebuildDropletNav(activeGalleryCount);

    if (this.deck) {
      this.deck.destroy();
      this.deck = null;
    }

    if (typeof window.CardDeck !== 'undefined') {
      this.deck = new window.CardDeck(deckEl);
      this._markScrollableSlides();
      this._checkBottomVisibility();
      this._updateDropletNav(0);
      this._initBackgroundPreloader();
    }
  }

  _rebuildDropletNav(activeCount) {
    if (!this.dropletNav) return;
    const capsule = this.dropletNav.querySelector('.pi__droplet-capsule');
    if (!capsule) return;

    if (activeCount <= 1) {
      this.dropletNav.style.display = 'none';
      return;
    }
    this.dropletNav.style.display = '';

    capsule.innerHTML = '';
    for (let i = 0; i < activeCount; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pi__droplet' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('data-droplet-dot', '');
      btn.setAttribute('data-slide-index', String(i));
      btn.setAttribute('aria-label', `Go to image ${i + 1} of ${activeCount}`);
      btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      btn.innerHTML = '<span class="pi__droplet-indicator"></span>';
      capsule.appendChild(btn);
    }
    this.dropletDots = this.querySelectorAll('[data-droplet-dot]');
    this._initDropletNav();
  }

  // Jump gallery to slide for variant
  _updateGallery(variant, opts) {
    opts = opts || {};
    if (!this.deck || !this.galleryContainer) return;

    const targetMediaId = this._mediaIdForVariant(variant);
    if (!targetMediaId) return;

    const matchingIdx = this.deck.slides.findIndex(
      (s) => s.getAttribute('data-media-id') === targetMediaId
    );
    if (matchingIdx < 0 || matchingIdx === this.deck.index) return;

    // Force jump even if settling
    this.deck.goTo(matchingIdx, { force: true, animate: opts.animate !== false });
    this._updateDropletNav(matchingIdx);
  }

  // Droplet navigation

  _initDropletNav() {
    if (!this.dropletDots || this.dropletDots.length === 0) return;

    this.dropletDots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const targetIdx = parseInt(dot.getAttribute('data-slide-index'), 10);
        if (this.deck && !isNaN(targetIdx)) {
          this.deck.goTo(targetIdx);
        }
      });
    });
  }

  _updateDropletNav(activeIdx) {
    if (!this.dropletDots || this.dropletDots.length === 0) return;
    const idx = typeof activeIdx === 'number' ? activeIdx : (this.deck ? this.deck.index : 0);

    const isBeyondGallery = idx >= this.dropletDots.length;
    if (this.dropletNav) {
      this.dropletNav.classList.toggle('is-beyond-gallery', isBeyondGallery);
    }

    this.dropletDots.forEach((dot) => {
      const dotIdx = parseInt(dot.getAttribute('data-slide-index'), 10);
      const isActive = dotIdx === idx;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  // Sheets

  _openSheet(sheet) {
    if (!sheet) return;
    this.backdrop?.classList.add('is-visible');
    sheet.classList.add('is-visible');
    document.body.classList.add('pi-sheet-open');
    document.body.style.overflow = 'hidden';
    // Pause deck gestures while sheet is open
    if (this.deck) this.deck.lock();
  }

  _closeAllSheets() {
    this.backdrop?.classList.remove('is-visible');
    this.cartSheet?.classList.remove('is-visible');
    this.detailsSheet?.classList.remove('is-visible');
    document.body.classList.remove('pi-sheet-open');
    document.body.style.overflow = '';
    if (this.deck) this.deck.unlock();
  }

  // Add to cart

  _addToCart() {
    if (this._isAddingToCart) return;

    const variant = this.variants[this.currentVariantIdx];
    if (!variant || !variant.available) return;

    this._isAddingToCart = true;

    // Loading state
    if (this.confirmBtn) {
      this.confirmBtn.classList.remove('pi__confirm-btn--default', 'pi__confirm-btn--success');
      this.confirmBtn.classList.add('pi__confirm-btn--loading');
    }
    if (this.confirmBtnText) {
      this.confirmBtnText.innerHTML = '<span class="pi__confirm-success-wrap">Adding to Bag...</span>';
    }

    const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) ? window.theme.routes.cart_add_url : '/cart/add';
    const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) ? window.theme.routes.cart : '/cart';

    const body = JSON.stringify({
      id: variant.id,
      quantity: this.quantity
    });

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
    .then(res => {
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    })
    .then(item => {
      // Success state
      if (this.confirmBtn) {
        this.confirmBtn.classList.remove('pi__confirm-btn--loading');
        this.confirmBtn.classList.add('pi__confirm-btn--success');
      }
      if (this.confirmBtnText) {
        this.confirmBtnText.innerHTML = '<span class="pi__confirm-success-wrap"><svg class="pi__confirm-check-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg> Added to Bag</span>';
      }

      // Dispatch cart update
      return fetch(cartUrl + '.js').then(r => r.json()).then(cart => {
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          detail: { cart: cart },
          bubbles: true
        }));
        return { item, cart };
      });
    })
    .then(({ item, cart }) => {
      this._closeAllSheets();

      setTimeout(() => {
        this._isAddingToCart = false;
        if (this.confirmBtn) {
          this.confirmBtn.classList.remove('pi__confirm-btn--success', 'pi__confirm-btn--loading');
          this.confirmBtn.classList.add('pi__confirm-btn--default');
        }
        this._updateQtyUI();

        if (cart) this._updateCartBadgeCount(cart.item_count);

        // Trigger cart pill feedback
        this._showCartPillFeedback(item, variant);
      }, 520);
    })
    .catch(err => {
      console.error('Add to cart error:', err);
      this._isAddingToCart = false;
      if (this.confirmBtn) {
        this.confirmBtn.classList.remove('pi__confirm-btn--loading', 'pi__confirm-btn--success');
        this.confirmBtn.classList.add('pi__confirm-btn--default');
      }
      this._updateQtyUI();
    });
  }

  // Cart pill feedback animation

  _showCartPillFeedback(item, variant) {
    if (!this.cartPill) return;

    let thumbUrl = '';
    if (item && item.image) {
      thumbUrl = item.image;
    } else if (item && item.featured_image && item.featured_image.url) {
      thumbUrl = item.featured_image.url;
    } else if (variant && variant.featured_media && variant.featured_media.preview_image) {
      thumbUrl = variant.featured_media.preview_image.src;
    } else if (this.productData && this.productData.featured_image) {
      thumbUrl = this.productData.featured_image;
    }

    if (this.cartPillThumb && thumbUrl) {
      this.cartPillThumb.src = thumbUrl;
    }

    this.cartPill.classList.remove('is-expanded');
    void this.cartPill.offsetWidth;
    this.cartPill.classList.add('is-expanded');

    if (this.cartPillTimeout) clearTimeout(this.cartPillTimeout);
    this.cartPillTimeout = setTimeout(() => {
      this.cartPill?.classList.remove('is-expanded');
      this.cartPillTimeout = null;
    }, 2800);
  }

  // Open cart drawer

  _openCart(e) {
    if (e) e.preventDefault();

    const cartDrawer = document.querySelector('[data-drawer="drawer-cart"]') || document.querySelector('#drawer-cart');
    if (cartDrawer) {
      cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      return;
    }

    const headerCartTrigger = document.querySelector('header [data-drawer-toggle="drawer-cart"], .header [data-drawer-toggle="drawer-cart"], [data-header-cart-count]');
    if (headerCartTrigger && headerCartTrigger !== this.cartBtn) {
      headerCartTrigger.click();
      return;
    }

    const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) ? window.theme.routes.cart : '/cart';
    window.location.href = cartUrl;
  }

  // Cart badge

  _updateCartBadge() {
    const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) ? window.theme.routes.cart : '/cart';
    fetch(cartUrl + '.js')
      .then(r => r.json())
      .then(cart => this._updateCartBadgeCount(cart.item_count))
      .catch(() => {});
  }

  _updateCartBadgeCount(count) {
    if (!this.cartBadge) return;
    const num = parseInt(count, 10) || 0;
    this.cartBadge.textContent = num;
    if (num > 0) {
      this.cartBadge.style.display = 'flex';
      this.cartBadge.classList.remove('is-popped');
      void this.cartBadge.offsetWidth;
      this.cartBadge.classList.add('is-popped');
      setTimeout(() => this.cartBadge?.classList.remove('is-popped'), 300);
    } else {
      this.cartBadge.style.display = 'none';
    }
  }

  // Money formatting

  _formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let format = (window.theme && window.theme.moneyFormat) ? window.theme.moneyFormat : '${{amount}}';
    format = format.replace(/<[^>]*>/g, '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function fmt(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var c = parts[1] ? (decimal + parts[1]) : '';
      return dollars + c;
    }

    var value = '';
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholderRegex, value).trim();
  }

  disconnectedCallback() {
    document.body.classList.remove('pi-sheet-open');
    if (this._onKeyDown) {
      document.removeEventListener('keydown', this._onKeyDown);
    }
    if (this._onFooterAccordionClick) {
      this.removeEventListener('click', this._onFooterAccordionClick);
    }
    if (this._onDeckResize) {
      window.removeEventListener('resize', this._onDeckResize);
    }
    if (this.atcBarBtn && this._onAtcPointerMove) {
      this.atcBarBtn.removeEventListener('pointermove', this._onAtcPointerMove);
      this.atcBarBtn.removeEventListener('pointerleave', this._onAtcPointerLeave);
      this.atcBarBtn.removeEventListener('pointerenter', this._onAtcPointerEnter);
    }
    if (this.deck) {
      this.deck.destroy();
      this.deck = null;
    }
  }
}

if (!customElements.get('product-immersive')) {
  customElements.define('product-immersive', ProductImmersive);
}
